import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useContent } from "../../../context/ContentContext";
import { Modal } from "../../ui/Modal/Modal";
import { FileUpload } from "../../ui/FileUpload/FileUpload";
import { FileUploadWorkspace } from "../../ui/FileUpload/FileUploadWorkspace";
import { MdEmail } from "react-icons/md";
import { FaNewspaper } from "react-icons/fa6";
import { HiDocumentText } from "react-icons/hi";
import { FaWhatsapp } from "react-icons/fa";
import { IoChevronDown, IoInformationCircle } from "react-icons/io5";
import { FileUploadAPI } from "../../../services/FileUploadAPI";
import { ErrorNotification } from "../../ui/ErrorNotification/ErrorNotification";
import { getWorkflowId } from "../../../context/ContentContext";
import "./CreateContentModal.css";

const PRODUCT_OPTIONS = ["Nubeqa", "Adempas", "Other"];

const CONTEXT_MODES = [
  { value: "file", label: "Provide a file as a source" },
  { value: "text", label: "Provide a context as a source" },
  { value: "both", label: "Both" },
];

const CONTENT_FORMATS = [
  { id: "email", label: "E-mail", icon: <MdEmail size={32} /> },
  { id: "separata", label: "Separata", icon: <HiDocumentText size={32} /> },
  { id: "news", label: "News", icon: <FaNewspaper size={32} /> },
  { id: "whatsapp", label: "WhatsApp", icon: <FaWhatsapp size={32} /> },
];

function resolveContextModeFromInitial(initialData) {
  if (!initialData) return "file";
  if (
    initialData.contextMode &&
    ["file", "text", "both"].includes(initialData.contextMode)
  ) {
    return initialData.contextMode;
  }
  const ctx = String(
    initialData.context ?? initialData.data?.context ?? "",
  ).trim();
  const hasF = Boolean(initialData.fileId);
  if (hasF && ctx) return "both";
  if (hasF) return "file";
  if (ctx) return "text";
  return "file";
}

export const CreateContentModal = ({
  isOpen,
  onClose,
  onOpenEmailSettings,
  initialData,
}) => {
  const navigate = useNavigate();
  const { startRun } = useContent();
  const [uploadKey, setUploadKey] = useState(0);
  const [contextMode, setContextMode] = useState("file");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingRun, setIsLoadingRun] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [selectedFormatLabel, setSelectedFormatLabel] = useState(null);
  const [formData, setFormData] = useState({
    bayerBrand: initialData?.bayerBrand || "",
    brand: initialData?.brand || "",
    outputLanguage: initialData?.outputLanguage || "",
    compliance: initialData?.compliance || "",
    context: initialData?.context ?? initialData?.data?.context ?? "",
  });
  const [toastError, setToastError] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [abortController, setAbortController] = useState(null);
  const options = ["Portuguese", "Spanish"];

  const getComplianceOptions = () => {
    if (formData.outputLanguage === "Portuguese") {
      return ["Brazil"];
    }
    if (formData.outputLanguage === "Spanish") {
      return ["Mexico", "No compliance"];
    }
    return ["Brazil", "Mexico", "No compliance"];
  };

  useEffect(() => {
    const validOptions = getComplianceOptions();

    if (formData.compliance && !validOptions.includes(formData.compliance)) {
      setFormData((prev) => ({ ...prev, compliance: "" }));
    }
  }, [formData.outputLanguage]);

  const brandFieldEnabled =
    formData.outputLanguage === "Portuguese" &&
    formData.compliance === "Brazil";

  const spanishRequiresFileOnlyContext = formData.outputLanguage === "Spanish";

  useEffect(() => {
    if (!brandFieldEnabled) {
      setFormData((prev) => (prev.brand ? { ...prev, brand: "" } : prev));
      setActiveDropdown((d) => (d === "brand" ? null : d));
    }
  }, [brandFieldEnabled]);

  useEffect(() => {
    if (initialData) {
      setFileId(initialData.fileId || null);
      setFileName(initialData.fileName || null);

      if (initialData.fileName) {
        setUploadedFile({
          name: initialData.fileName,
          size: initialData.fileSize || 0,
        });
      } else {
        setUploadedFile(null);
      }

      setSelectedFormat(initialData.selectedFormat || null);
      setSelectedFormatLabel(initialData.selectedFormatLabel || null);
      setContextMode(resolveContextModeFromInitial(initialData));
      setFormData({
        bayerBrand: initialData.bayerBrand || null,
        brand: initialData.brand || "",
        outputLanguage: initialData.outputLanguage || null,
        compliance: initialData.compliance || "",
        context: initialData.context ?? initialData.data?.context ?? "",
      });
    }
  }, [initialData]);

  const handleFileSelect = async (file) => {
    if (!file) {
      setUploadedFile(null);
      setFileId(null);
      setFileName(null);
      setUploadKey((prev) => prev + 1);
      return;
    }

    const controller = new AbortController();
    setAbortController(controller);
    setIsUploading(true);
    setToastError(null);
    setUploadedFile(file);

    try {
      const result = await FileUploadAPI.uploadFile(
        file,
        {
          purpose: "assistants",
          path: "/",
          extract_images: "false",
        },
        controller.signal,
      );

      if (controller.signal.aborted) {
        return;
      }

      const resultFile = result[0];
      const newFileId =
        resultFile.file_id || resultFile.task_id || resultFile.id;
      const newFileName = resultFile.filename;

      await FileUploadAPI.checkFileExists(newFileId, controller.signal);

      if (controller.signal.aborted) {
        return;
      }

      setFileId(newFileId);
      setFileName(newFileName);
    } catch (error) {
      if (error.name === "AbortError" || controller.signal.aborted) {
        return;
      }
      setToastError(
        "Upload failed or file could not be verified. Please try again.",
      );
      setUploadedFile(null);
      setFileId(null);
      setFileName(null);
      setUploadKey((prev) => prev + 1);
    } finally {
      if (!controller.signal.aborted) {
        setIsUploading(false);
        setAbortController(null);
      }
    }
  };

  const handleWorkspaceFileSelect = (file) => {
    setFileId(file.id);
    setFileName(file.name);
    setUploadedFile({
      name: file.name,
      size: 0,
      fromWorkspace: true,
    });
  };

  const handleCancelUpload = () => {
    if (abortController) {
      abortController.abort();
    }
    setIsUploading(false);
    setUploadedFile(null);
    setFileId(null);
    setFileName(null);
    setAbortController(null);
    setUploadKey((prev) => prev + 1);
  };

  const selectFormat = (formatId) => {
    const selected = CONTENT_FORMATS.find((f) => f.id === formatId);
    setSelectedFormat(formatId);
    setSelectedFormatLabel(selected ? selected.label : null);
  };

  const handleContinue = async () => {
    const configData = {
      contextMode,
      sourceType: contextMode === "text" ? "prompt" : "file",
      uploadedFile,
      selectedFormat,
      selectedFormatLabel,
      ...formData,
      context: formData.context,
      fileId,
      fileName,
    };

    if (selectedFormat === "email") {
      onOpenEmailSettings?.(configData);
      onClose();
    } else {
      setIsLoadingRun(true);
      try {
        const workflowId = getWorkflowId(
          formData.compliance,
          selectedFormat,
          null,
        );

        startRun({ ...configData, workflowId });

        onClose();
        navigate("/run-progress");
      } catch (error) {
        console.error("Error starting run:", error);
        setToastError("Failed to start workflow. Please try again.");
      } finally {
        setIsLoadingRun(false);
      }
    }
  };

  const complianceOptions = getComplianceOptions();

  const isPtBr =
    formData.outputLanguage === "Portuguese" &&
    formData.compliance === "Brazil";
  const hasFileReady = Boolean(fileId && fileName);
  const hasContext = Boolean(
    formData.context && String(formData.context).trim(),
  );

  const contextRequirementMet = (() => {
    if (contextMode === "file") return hasFileReady;
    if (contextMode === "text") {
      if (!isPtBr) return false;
      return hasContext;
    }
    if (contextMode === "both") {
      if (!hasFileReady) return false;
      if (isPtBr && !hasContext) return false;
      return true;
    }
    return false;
  })();

  const canContinue =
    Boolean(selectedFormat) &&
    !isUploading &&
    Boolean(formData.outputLanguage) &&
    Boolean(formData.compliance) &&
    (!brandFieldEnabled || Boolean(formData.brand)) &&
    contextRequirementMet;

  const fileColumnDisabled = contextMode === "text";
  const textColumnDisabled = contextMode === "file";

  return (
    <>
      {toastError && (
        <ErrorNotification
          message={toastError}
          onClose={() => setToastError(null)}
        />
      )}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Create new content"
        size="xlarge"
      >
        <div className="create-content-modal">
          <p className="create-content-modal__mandatory">*Mandatory</p>

          <div className="create-content-modal__section">
            <div className="create-content-modal__row">
              <div className="create-content-modal__field">
                <label className="create-content-modal__label">
                  Output language <span className="orange-asterisk">*</span>
                </label>
                <div className="select-wrapper">
                  <div
                    className={`select-trigger ${activeDropdown === "language" ? "open" : ""}`}
                    onClick={() =>
                      setActiveDropdown(
                        activeDropdown === "language" ? null : "language",
                      )
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter" && e.key !== " ") return;
                      e.preventDefault();
                      setActiveDropdown(
                        activeDropdown === "language" ? null : "language",
                      );
                    }}
                  >
                    <span
                      className={
                        !formData.outputLanguage
                          ? "select-trigger__placeholder"
                          : ""
                      }
                    >
                      {formData.outputLanguage || "Select language"}
                    </span>
                    <IoChevronDown
                      className={`select-arrow-icon ${activeDropdown === "language" ? "rotate" : ""}`}
                    />
                  </div>
                  {activeDropdown === "language" && (
                    <ul className="options-menu">
                      {options.map((lang) => (
                        <li
                          key={lang}
                          className="option-item"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              outputLanguage: lang,
                            }));
                            setActiveDropdown(null);
                          }}
                        >
                          {lang}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="create-content-modal__field">
                <label className="create-content-modal__label">
                  Compliance review <span className="orange-asterisk">*</span>
                </label>
                <div className="select-wrapper">
                  <div
                    className={`select-trigger ${!formData.outputLanguage ? "select-trigger--awaiting-prev" : ""} ${
                      activeDropdown === "compliance" ? "open" : ""
                    }`}
                    onClick={() => {
                      if (!formData.outputLanguage) return;
                      setActiveDropdown(
                        activeDropdown === "compliance" ? null : "compliance",
                      );
                    }}
                    role="button"
                    tabIndex={formData.outputLanguage ? 0 : -1}
                    aria-disabled={!formData.outputLanguage}
                  >
                    <span
                      className={
                        !formData.compliance
                          ? "select-trigger__placeholder"
                          : ""
                      }
                    >
                      {formData.outputLanguage
                        ? formData.compliance || "Select compliance"
                        : "Select compliance"}
                    </span>
                    <IoChevronDown
                      className={`select-arrow-icon ${activeDropdown === "compliance" ? "rotate" : ""}`}
                    />
                  </div>
                  {activeDropdown === "compliance" &&
                    formData.outputLanguage && (
                      <ul className="options-menu">
                        {complianceOptions.map((compli) => (
                          <li
                            key={compli}
                            className="option-item"
                            onClick={() => {
                              setFormData((prev) => ({
                                ...prev,
                                compliance: compli,
                              }));
                              setActiveDropdown(null);
                            }}
                          >
                            {compli}
                          </li>
                        ))}
                      </ul>
                    )}
                </div>
              </div>

              <div
                className={`create-content-modal__field create-content-modal__brand-field ${
                  !brandFieldEnabled
                    ? "create-content-modal__brand-field--disabled"
                    : ""
                }`}
              >
                <label className="create-content-modal__label">
                  Product <span className="orange-asterisk">*</span>
                </label>
                <div className="select-wrapper">
                  <div
                    className={`select-trigger ${brandFieldEnabled && activeDropdown === "brand" ? "open" : ""} ${
                      !brandFieldEnabled ? "select-trigger--brand-disabled" : ""
                    }`}
                    onClick={() => {
                      if (!brandFieldEnabled) return;
                      setActiveDropdown(
                        activeDropdown === "brand" ? null : "brand",
                      );
                    }}
                    role="button"
                    tabIndex={brandFieldEnabled ? 0 : -1}
                    onKeyDown={(e) => {
                      if (
                        !brandFieldEnabled ||
                        (e.key !== "Enter" && e.key !== " ")
                      )
                        return;
                      e.preventDefault();
                      setActiveDropdown(
                        activeDropdown === "brand" ? null : "brand",
                      );
                    }}
                    aria-expanded={Boolean(
                      brandFieldEnabled && activeDropdown === "brand",
                    )}
                    aria-disabled={!brandFieldEnabled}
                  >
                    <span
                      className={
                        !formData.brand && brandFieldEnabled
                          ? "select-trigger__placeholder"
                          : ""
                      }
                    >
                      {brandFieldEnabled
                        ? formData.brand || "Select product"
                        : "Select product"}
                    </span>
                    <IoChevronDown
                      className={`select-arrow-icon ${activeDropdown === "brand" ? "rotate" : ""}`}
                    />
                  </div>
                  {brandFieldEnabled && activeDropdown === "brand" && (
                    <ul className="options-menu">
                      {PRODUCT_OPTIONS.map((name) => (
                        <li
                          key={name}
                          className="option-item"
                          onClick={() => {
                            setFormData((prev) => ({ ...prev, brand: name }));
                            setActiveDropdown(null);
                          }}
                        >
                          {name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="create-content-modal__section create-content-modal__panel create-content-modal__context-panel">
            <div className="create-content-modal__panel-heading">
              <span className="create-content-modal__panel-title">
                Provide a content source
                <span className="orange-asterisk">*</span>
              </span>
              <span className="create-content-modal__info-wrap">
                <button
                  type="button"
                  className="create-content-modal__info-tip"
                  aria-describedby="create-content-context-tooltip"
                  aria-label="Information about scientific studies and context"
                >
                  <IoInformationCircle />
                </button>
                <span
                  id="create-content-context-tooltip"
                  className="create-content-modal__info-tooltip"
                  role="tooltip"
                >
                  <span className="create-content-modal__info-tooltip-lead">
                    The scientific studies for Bayer products available on MIA
                    Content have already been uploaded.
                  </span>
                  <span className="create-content-modal__info-tooltip-rest">
                    It is not necessary to include them as context for
                    generating new content.
                  </span>
                </span>
              </span>
            </div>

            <div
              className="create-content-modal__context-mode"
              role="radiogroup"
              aria-label="Context source"
            >
              {CONTEXT_MODES.map(({ value, label }) => (
                <label
                  key={value}
                  className={`create-content-modal__mode-label ${contextMode === value ? "create-content-modal__mode-label--active" : ""}`}
                >
                  <input
                    type="radio"
                    name="contextMode"
                    value={value}
                    checked={contextMode === value}
                    onChange={() => setContextMode(value)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>

            {spanishRequiresFileOnlyContext && (
              <p className="create-content-modal__context-hint">
                For Spanish (Mexico / No compliance), a source file is required
                — choose &quot;Provide a file as a source&quot;.
              </p>
            )}

            <div
              className="create-content-modal__context-subsection"
              aria-hidden="false"
            >
              <div
                className="create-content-modal__context-h-rule"
                role="presentation"
              />

              <div className="create-content-modal__context-columns">
                <div className="create-content-modal__context-col create-content-modal__context-col--file">
                  <p
                    className={`create-content-modal__context-block-title ${
                      fileColumnDisabled
                        ? "create-content-modal__context-block-title--muted"
                        : ""
                    }`}
                  >
                    Context from a file
                  </p>
                  <div
                    className={
                      fileColumnDisabled
                        ? "create-content-modal__context-interactive create-content-modal__context-interactive--locked"
                        : "create-content-modal__context-interactive"
                    }
                  >
                    <div
                      className="create-content-modal__file-stack"
                      key={uploadKey}
                    >
                      <FileUpload
                        onFileSelect={handleFileSelect}
                        isLoading={isUploading}
                        initialFile={uploadedFile}
                        onCancelUpload={handleCancelUpload}
                        supportedTypes="PDF, text, Markdown, Audio .mp3"
                      />
                    </div>
                    <FileUploadWorkspace
                      onFileSelect={handleWorkspaceFileSelect}
                      triggerLabel="Select previously uploaded file"
                      triggerClassName="create-content-modal__workspace-btn"
                    />
                  </div>
                </div>

                <div className="create-content-modal__context-col create-content-modal__context-col--text">
                  <p
                    className={`create-content-modal__context-block-title ${
                      textColumnDisabled
                        ? "create-content-modal__context-block-title--muted"
                        : ""
                    }`}
                  >
                    Context from a text
                  </p>
                  <div
                    className={
                      textColumnDisabled
                        ? "create-content-modal__context-interactive create-content-modal__context-interactive--locked"
                        : "create-content-modal__context-interactive"
                    }
                  >
                    <textarea
                      className="create-content-modal__textarea create-content-modal__textarea--dual"
                      rows={5}
                      placeholder="Write more details here about the content you want to create..."
                      value={formData.context}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          context: e.target.value,
                        }))
                      }
                      disabled={textColumnDisabled}
                      aria-label="Written context"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="create-content-modal__section create-content-modal__panel">
            <label className="create-content-modal__formats-heading">
              Content formats <span className="orange-asterisk">*</span>
            </label>
            <div className="create-content-modal__formats">
              {CONTENT_FORMATS.map((format) => (
                <button
                  key={format.id}
                  type="button"
                  className={`content-format ${selectedFormat === format.id ? "content-format--selected" : ""}`}
                  onClick={() => selectFormat(format.id)}
                >
                  <div className="content-format__icon">{format.icon}</div>
                  <span className="content-format__label">{format.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="create-content-modal__actions">
            <button
              type="button"
              className="button button--gradient-orange button--large buttons-pattern"
              onClick={handleContinue}
              disabled={!canContinue || isLoadingRun}
            >
              <span className="button__text">
                {isLoadingRun ? "Loading run…" : "Continue"}
              </span>
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
